/**
 * RichTextRenderer — shared markdown + widgets + visualization renderer.
 *
 * Single source of truth for rendering LLM-produced content. Used by:
 *   - chat-platform message blocks (via MessageBlockRenderer)
 *   - analysis insight detail body
 *   - any other module that needs to display streamed/stored agent text
 *
 * Handles:
 *   - Full GFM markdown (tables, task lists, strikethrough)
 *   - Line breaks preserved
 *   - Math expressions via KaTeX
 *   - Raw HTML (sanitised by default rehype-raw rules)
 *   - Syntax-highlighted code blocks (Prism, light theme)
 *   - External links auto-target=_blank
 *   - Embedded visualizations: `visualization:visual_abc123`
 *   - Embedded widgets (static + realtime): `<widget ...></widget>`
 *   - Legacy artifacts: `[artifact:chart:id]`
 */

import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import 'katex/dist/katex.min.css';

import { parseContentWithWidgets } from '../utils/widgetParser';
import ChartBlock from './ChartBlock';
import WidgetRenderer, { WidgetSkeleton } from './widgets/WidgetRenderer';
import VisualizeWidget from './widgets/VisualizeWidget';
import { ProseTable, ProseTHead, ProseTBody, ProseTR, ProseTH, ProseTD } from './markdown/ProseTableComponents';

const DEFAULT_PROSE =
    'text-[16px] prose max-w-none ' +
    'prose-p:my-4 prose-p:leading-[1.75] prose-p:text-gray-700 ' +
    'prose-h1:text-[19px] prose-h1:font-semibold prose-h1:mt-8 prose-h1:mb-2 prose-h1:leading-[1.7] prose-h1:text-gray-900 ' +
    'prose-h2:text-[17px] prose-h2:font-semibold prose-h2:mt-8 prose-h2:mb-2 prose-h2:leading-[1.7] prose-h2:text-gray-900 ' +
    'prose-h3:text-[16px] prose-h3:font-semibold prose-h3:mt-8 prose-h3:mb-1.5 prose-h3:leading-[1.7] prose-h3:text-gray-900 ' +
    'prose-h4:text-[15px] prose-h4:font-semibold prose-h4:mt-8 prose-h4:mb-1.5 prose-h4:leading-[1.7] prose-h4:text-gray-900 ' +
    'prose-ul:pl-6 prose-ul:my-4 prose-ul:leading-[1.75] ' +
    'prose-ol:pl-6 prose-ol:my-4 prose-ol:leading-[1.75] ' +
    'prose-li:my-1 prose-li:leading-[1.75] prose-li:text-gray-700 ' +
    'prose-strong:font-semibold prose-strong:text-gray-900 ' +
    'prose-em:text-[#1F2937] ' +
    'prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline ' +
    'prose-blockquote:border-l-4 prose-blockquote:border-[#E2E8F0] prose-blockquote:text-[#475569] ' +
    'prose-code:text-[13px] prose-code:bg-[#F1F5F9] prose-code:text-[#BE123C] prose-code:px-1 prose-code:rounded prose-code:before:content-none prose-code:after:content-none ' +
    'prose-hr:my-6 prose-hr:border-[#E2E8F0] ' +
    'prose-img:rounded-[8px] prose-img:border prose-img:border-[#EDEFF3]';

function CodeBlock({ inline, className, children, ...rest }) {
    const match = /language-(\w+)/.exec(className || '');
    if (inline || !match) {
        return (
            <code className={className} {...rest}>
                {children}
            </code>
        );
    }
    return (
        <div className="my-3 rounded-[10px] border border-[#E2E8F0] overflow-hidden">
            <div className="flex items-center justify-between bg-[#F8FAFC] text-[11px] text-[#6B7280] px-[12px] py-[6px] border-b border-[#E2E8F0]">
                <span className="font-mono">{match[1]}</span>
            </div>
            <SyntaxHighlighter
                language={match[1]}
                style={oneLight}
                PreTag="div"
                customStyle={{ margin: 0, padding: '12px 14px', fontSize: '12.5px', background: '#FFFFFF' }}
            >
                {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
        </div>
    );
}

function ExternalLink({ href, children }) {
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 no-underline hover:underline">
            {children}
        </a>
    );
}

const DEFAULT_COMPONENTS = {
    code: CodeBlock,
    a: ExternalLink,
    table: ProseTable,
    thead: ProseTHead,
    tbody: ProseTBody,
    tr: ProseTR,
    th: ProseTH,
    td: ProseTD,
};

function MarkdownSegment({ text, components }) {
    if (!text) return null;
    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
            rehypePlugins={[rehypeRaw, rehypeKatex]}
            components={components}
        >
            {text}
        </ReactMarkdown>
    );
}

export default function RichTextRenderer({
    text,
    className = '',
    prose = DEFAULT_PROSE,
    components = DEFAULT_COMPONENTS,
    isStreaming = false,
}) {
    const parts = useMemo(
        () => parseContentWithWidgets(text || '', isStreaming),
        [text, isStreaming],
    );
    if (!text) return null;
    return (
        <div className={`${prose} ${className}`.trim()}>
            {parts.map((part, i) => {
                if (part.type === 'visualization') {
                    return (
                        <div key={`viz-${i}`} className="my-3">
                            <VisualizeWidget artifactId={part.artifactId} />
                        </div>
                    );
                }
                if (part.type === 'widget') {
                    return <WidgetRenderer key={`widget-${i}`} config={part.config} />;
                }
                if (part.type === 'widget_loading') {
                    return (
                        <div key={`widget-loading-${i}`} className="my-3">
                            <WidgetSkeleton type={part.widgetType} />
                        </div>
                    );
                }
                if (part.type === 'artifact' && part.artifactType === 'chart') {
                    return <ChartBlock key={`chart-${i}`} block={{ artifact_id: part.artifactId }} />;
                }
                return <MarkdownSegment key={`md-${i}`} text={part.content} components={components} />;
            })}
        </div>
    );
}

export { DEFAULT_PROSE, DEFAULT_COMPONENTS, CodeBlock, ExternalLink };
